// VERSION: Agrupado por clientes - 2025-10-03
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { auditTransitApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import CreateProgrammedAuditForm from '../components/CreateProgrammedAuditForm';

interface Auditoria {
  id: number;
  titulo: string;
  descripcion: string;
  fechaSolicitada: string;
  fechaSolicitadaOriginal: string;
  fechaProgramada: string | null;
  prioridad: 'normal' | 'prioritaria' | 'urgente';
  estado: string;
  origenCarga: 'cliente' | 'programada' | 'normal';
  validada: boolean;
  client: {
    id: number;
    nombre: string;
    email: string;
  };
  branch: {
    id: number;
    nombre: string;
    direccion: string;
  };
  creator: {
    id: number;
    name: string;
    email: string;
    role: string;
  };
  operador: {
    id: number;
    name: string;
    email: string;
  } | null;
  createdAt: string;
}

interface Stats {
  total: number;
  porOrigen: {
    cliente: number;
    programada: number;
    normal: number;
  };
  validadas: number;
  pendientesValidacion: number;
}

const ColaGestionPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [auditorias, setAuditorias] = useState<Auditoria[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtroOrigen, setFiltroOrigen] = useState<string>('');
  const [selectedAudit, setSelectedAudit] = useState<number | null>(null);
  const [selectedAuditForEdit, setSelectedAuditForEdit] = useState<Auditoria | null>(null);
  const [expandedRow, setExpandedRow] = useState<{ id: number; action: 'edit' | 'habilitar' } | null>(null);
  const [seleccionadas, setSeleccionadas] = useState<number[]>([]);
  const [operadores, setOperadores] = useState<any[]>([]);
  const [showHabilitarModal, setShowHabilitarModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCrearAuditoriaModal, setShowCrearAuditoriaModal] = useState(false);
  const [editData, setEditData] = useState({
    titulo: '',
    descripcion: '',
    fechaSolicitada: '',
    prioridad: 'normal' as 'normal' | 'prioritaria' | 'urgente'
  });
  const [nuevaAuditoria, setNuevaAuditoria] = useState({
    clienteId: '',
    branchId: '',
    titulo: '',
    descripcion: '',
    fechaProgramada: '',
    prioridad: 'normal' as 'normal' | 'prioritaria' | 'urgente'
  });
  const [clientes, setClientes] = useState<any[]>([]);
  const [sucursales, setSucursales] = useState<any[]>([]);
  const [habilitarData, setHabilitarData] = useState({
    operadorId: '',
    fechaProgramada: '',
    prioridad: ''
  });

  useEffect(() => {
    cargarDatos();
    cargarOperadores();
    cargarClientes();
  }, [filtroOrigen]);

  const cargarClientes = async () => {
    try {
      const response = await api.get('/clients');
      setClientes(response.data?.data || response.data || []);
    } catch (err) {
      console.error('Error cargando clientes:', err);
    }
  };

  const cargarSucursales = async (clienteId: string) => {
    if (!clienteId) {
      setSucursales([]);
      return;
    }
    try {
      const response = await api.get(`/clients/${clienteId}/branches`);
      setSucursales(response.data?.data || response.data || []);
    } catch (err) {
      console.error('Error cargando sucursales:', err);
      setSucursales([]);
    }
  };

  const cargarDatos = async () => {
    try {
      setLoading(true);
      console.log('🚦 [DEBUG] Iniciando carga de datos...');
      console.log('🚦 [DEBUG] auditTransitApi:', auditTransitApi);
      console.log('🚦 [DEBUG] getColaGestion:', auditTransitApi?.getColaGestion);

      if (!auditTransitApi || !auditTransitApi.getColaGestion) {
        throw new Error('auditTransitApi.getColaGestion no está disponible');
      }

      const params = filtroOrigen ? { origen: filtroOrigen } : {};
      console.log('🚦 [DEBUG] Llamando a getColaGestion con params:', params);
      const response = await auditTransitApi.getColaGestion(params);
      console.log('🚦 [DEBUG] Respuesta recibida:', response);
      setAuditorias(response.data.data);
      setStats(response.data.stats);
      setError(null);
    } catch (err: any) {
      const errorMsg = err.message || err.response?.data?.message || 'Error cargando cola de gestión';
      console.error('❌ [ERROR]:', err);
      console.error('❌ [ERROR] Mensaje:', errorMsg);
      console.error('❌ [ERROR] Stack:', err.stack);
      setError(`ERROR: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const cargarOperadores = async () => {
    try {
      console.log('🔧 Cargando operadores...');
      const response = await api.get('/users/operators');
      console.log('🔧 Respuesta operadores:', response.data);
      const ops = response.data.data || [];
      console.log('🔧 Operadores cargados:', ops);
      setOperadores(ops);
    } catch (err) {
      console.error('❌ Error cargando operadores:', err);
    }
  };

  const handleEditarAuditoria = (audit: Auditoria) => {
    setSelectedAuditForEdit(audit);
    setEditData({
      titulo: audit.titulo,
      descripcion: audit.descripcion,
      fechaSolicitada: audit.fechaSolicitadaOriginal || audit.fechaSolicitada.split('T')[0],
      prioridad: audit.prioridad
    });
    setExpandedRow({ id: audit.id, action: 'edit' });
  };

  const confirmarEditar = async () => {
    if (!selectedAuditForEdit) return;

    try {
      await api.put(`/audits/${selectedAuditForEdit.id}`, editData);
      alert('Auditoría actualizada exitosamente');
      setExpandedRow(null);
      setSelectedAuditForEdit(null);
      cargarDatos();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error actualizando auditoría');
    }
  };

  const handleEliminarAuditoria = async (audit: Auditoria) => {
    if (!window.confirm(`¿Eliminar la auditoría "${audit.titulo}"? Esta acción no se puede deshacer.`)) return;

    try {
      await api.delete(`/audits/${audit.id}`, { data: { type: 'cleanup' } });
      alert('Auditoría eliminada exitosamente');
      setExpandedRow(null);
      cargarDatos();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error eliminando auditoría');
    }
  };

  const toggleSeleccion = (id: number) => {
    setSeleccionadas((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSeleccionarTodas = () => {
    setSeleccionadas((prev) =>
      prev.length === auditorias.length ? [] : auditorias.map((a) => a.id)
    );
  };

  const handleEliminarSeleccionadas = async () => {
    if (seleccionadas.length === 0) return;
    if (!window.confirm(`¿Eliminar ${seleccionadas.length} auditoría(s) seleccionada(s)? Esta acción no se puede deshacer.`)) return;

    try {
      await Promise.all(
        seleccionadas.map((id) => api.delete(`/audits/${id}`, { data: { type: 'cleanup' } }))
      );
      alert(`${seleccionadas.length} auditoría(s) eliminada(s) exitosamente`);
      setSeleccionadas([]);
      setExpandedRow(null);
      cargarDatos();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error eliminando auditorías');
      cargarDatos();
    }
  };

  const crearAuditoriaProgramable = async () => {
    if (!nuevaAuditoria.clienteId || !nuevaAuditoria.branchId || !nuevaAuditoria.fechaProgramada) {
      alert('Por favor completá todos los campos obligatorios');
      return;
    }

    try {
      await api.post('/audits', {
        clientId: parseInt(nuevaAuditoria.clienteId),
        branchId: parseInt(nuevaAuditoria.branchId),
        titulo: nuevaAuditoria.titulo || `Auditoría programada`,
        descripcion: nuevaAuditoria.descripcion,
        fechaSolicitada: nuevaAuditoria.fechaProgramada,
        fechaSolicitadaOriginal: nuevaAuditoria.fechaProgramada,
        prioridad: nuevaAuditoria.prioridad,
        origenCarga: 'programada',
        estado: 'en_transito'
      });

      alert('Auditoría programable creada exitosamente');
      setShowCrearAuditoriaModal(false);
      setNuevaAuditoria({
        clienteId: '',
        branchId: '',
        titulo: '',
        descripcion: '',
        fechaProgramada: '',
        prioridad: 'normal'
      });
      setSucursales([]);
      cargarDatos();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error creando auditoría');
    }
  };

  const handleHabilitarTransito = async (auditId: number) => {
    setSelectedAudit(auditId);
    setHabilitarData({ operadorId: '', fechaProgramada: '', prioridad: '' });
    setExpandedRow({ id: auditId, action: 'habilitar' });
  };

  const confirmarHabilitar = async () => {
    if (!selectedAudit) return;

    try {
      const data: any = {};
      if (habilitarData.operadorId) data.operadorId = parseInt(habilitarData.operadorId);
      if (habilitarData.fechaProgramada) data.fechaProgramada = habilitarData.fechaProgramada;
      if (habilitarData.prioridad) data.prioridad = habilitarData.prioridad;

      await auditTransitApi.habilitarTransito(selectedAudit, data);
      alert('Auditoría habilitada para tránsito exitosamente');
      setExpandedRow(null);
      setHabilitarData({ operadorId: '', fechaProgramada: '', prioridad: '' });
      cargarDatos();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error habilitando auditoría');
    }
  };

  const handlePausarTransito = async (auditId: number) => {
    const motivo = prompt('Motivo para pausar esta auditoría:');
    if (!motivo) return;

    try {
      await auditTransitApi.pausarTransito(auditId, { motivo });
      alert('Auditoría pausada exitosamente');
      cargarDatos();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error pausando auditoría');
    }
  };

  const getOrigenBadge = (origen: string) => {
    const badges = {
      cliente: 'bg-blue-100 text-blue-800',
      programada: 'bg-purple-100 text-purple-800',
      normal: 'bg-gray-100 text-gray-800'
    };
    return badges[origen as keyof typeof badges] || 'bg-gray-100 text-gray-800';
  };

  const getPrioridadBadge = (prioridad: string) => {
    const badges = {
      normal: 'bg-green-100 text-green-800',
      prioritaria: 'bg-yellow-100 text-yellow-800',
      urgente: 'bg-red-100 text-red-800'
    };
    return badges[prioridad as keyof typeof badges] || 'bg-gray-100 text-gray-800';
  };

  // Las auditorías ya vienen ordenadas por fecha (más antiguas primero) desde el backend

  if (loading) {
    return <div className="flex justify-center items-center h-64">Cargando...</div>;
  }

  return (
    <div className="p-6 bg-white min-h-screen">
      <div className="mb-3">
        <h2 className="text-lg font-bold text-gray-900">🚦 Cola de Gestión de Auditorías</h2>
        <p className="text-xs text-gray-500">Gestiona las auditorías antes de que entren al flujo de trabajo normal</p>
      </div>

      {/* Estadísticas con paneles de colores */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {/* Total en Cola */}
          <div style={{
            background: '#2c3e50',
            borderRadius: '8px',
            padding: '20px',
            color: 'white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '5px' }}>
              📊 {stats.total}
            </div>
            <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Total en Cola</div>
            <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '5px' }}>• Mostrando todas</div>
          </div>

          {/* De Clientes */}
          <div style={{
            background: '#3498db',
            borderRadius: '8px',
            padding: '20px',
            color: 'white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '5px' }}>
              📋 {stats.porOrigen.cliente}
            </div>
            <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>De Clientes</div>
          </div>

          {/* Programadas */}
          <div style={{
            background: '#9b59b6',
            borderRadius: '8px',
            padding: '20px',
            color: 'white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '5px' }}>
              📅 {stats.porOrigen.programada}
            </div>
            <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Programadas</div>
          </div>

          {/* Pendientes */}
          <div style={{
            background: '#e67e22',
            borderRadius: '8px',
            padding: '20px',
            color: 'white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '5px' }}>
              ⏳ {stats.pendientesValidacion}
            </div>
            <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Pendientes</div>
          </div>
        </div>
      )}

      {/* Filtros y acciones */}
      <div className="bg-gray-50 p-3 rounded border mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-700">Filtrar:</span>
          <select
            value={filtroOrigen}
            onChange={(e) => setFiltroOrigen(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-xs"
          >
            <option value="">Todos</option>
            <option value="cliente">Clientes</option>
            <option value="programada">Programadas</option>
            <option value="normal">Normal</option>
          </select>
          <button
            onClick={cargarDatos}
            className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs"
          >
            🔄 Actualizar
          </button>
          {seleccionadas.length > 0 && (
            <button
              onClick={handleEliminarSeleccionadas}
              style={{
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                padding: '4px 10px',
                fontWeight: 600
              }}
            >
              🗑 Eliminar seleccionadas ({seleccionadas.length})
            </button>
          )}
        </div>
        <button
          onClick={() => setShowCrearAuditoriaModal(true)}
          style={{
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            color: 'white',
            padding: '0.75rem 1.5rem',
            borderRadius: '0.5rem',
            fontWeight: 'bold',
            fontSize: '0.95rem',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 4px 6px -1px rgba(245, 158, 11, 0.3)',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 8px -1px rgba(245, 158, 11, 0.4)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(245, 158, 11, 0.3)';
          }}
        >
          📅 Crear Auditoría Programable
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-300 text-red-700 px-3 py-2 rounded mb-3 text-xs">
          {error}
        </div>
      )}

      {/* Lista de Auditorías ordenadas por fecha (más antiguas primero) */}
      <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
        {auditorias.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            No hay auditorías en la cola de gestión
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>
                    <input
                      type="checkbox"
                      checked={auditorias.length > 0 && seleccionadas.length === auditorias.length}
                      onChange={toggleSeleccionarTodas}
                      style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                      title="Seleccionar todas"
                    />
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>🏢 Cliente</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>Auditoría</th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>Prioridad</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>Sucursal</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>Fecha Turno</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>Fecha Solicitada</th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {auditorias.map((audit, index) => (
                  <React.Fragment key={audit.id}>
                    <tr style={{
                      borderBottom: expandedRow?.id === audit.id ? 'none' : '1px solid #f3f4f6',
                      background: index % 2 === 0 ? 'white' : '#fafafa'
                    }}>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={seleccionadas.includes(audit.id)}
                          onChange={() => toggleSeleccion(audit.id)}
                          style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                        />
                      </td>
                      <td style={{ padding: '12px', fontWeight: '600', color: '#1f2937' }}>
                        {audit.client?.nombre || 'Sin Cliente'}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ fontWeight: '500' }}>{audit.titulo}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          {audit.origenCarga === 'cliente' ? '👤 Cliente' :
                           audit.origenCarga === 'programada' ? '📅 Programada' : '📋 Normal'}
                        </div>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          background: audit.prioridad === 'urgente' ? '#fef2f2' :
                                    audit.prioridad === 'prioritaria' ? '#fef3c7' : '#f0fdf4',
                          color: audit.prioridad === 'urgente' ? '#dc2626' :
                                audit.prioridad === 'prioritaria' ? '#ca8a04' : '#166534'
                        }}>
                          {audit.prioridad?.toUpperCase() || 'NORMAL'}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>{audit.branch?.nombre || '-'}</td>
                      <td style={{ padding: '12px' }}>
                        {audit.fechaSolicitadaOriginal
                          ? new Date(audit.fechaSolicitadaOriginal + 'T00:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                          : new Date(audit.fechaSolicitada).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px', color: '#6b7280' }}>
                        {new Date(audit.createdAt).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <button
                          onClick={() => expandedRow?.id === audit.id && expandedRow?.action === 'habilitar'
                            ? setExpandedRow(null)
                            : handleHabilitarTransito(audit.id)}
                          style={{
                            marginRight: '5px',
                            padding: '6px 12px',
                            background: expandedRow?.id === audit.id && expandedRow?.action === 'habilitar' ? '#6b7280' : '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          {expandedRow?.id === audit.id && expandedRow?.action === 'habilitar' ? '✕ Cerrar' : '✓ Habilitar'}
                        </button>
                        <button
                          onClick={() => expandedRow?.id === audit.id && expandedRow?.action === 'edit'
                            ? setExpandedRow(null)
                            : handleEditarAuditoria(audit)}
                          style={{
                            padding: '6px 12px',
                            background: expandedRow?.id === audit.id && expandedRow?.action === 'edit' ? '#6b7280' : '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          {expandedRow?.id === audit.id && expandedRow?.action === 'edit' ? '✕ Cerrar' : '✎ Editar'}
                        </button>
                        <button
                          onClick={() => handleEliminarAuditoria(audit)}
                          style={{
                            marginLeft: '5px',
                            padding: '6px 12px',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          🗑 Eliminar
                        </button>
                      </td>
                    </tr>
                    {/* Fila expandida para Habilitar */}
                    {expandedRow?.id === audit.id && expandedRow?.action === 'habilitar' && (
                      <tr>
                        <td colSpan={8} style={{ padding: '0', background: '#f0fdf4', borderBottom: '2px solid #10b981' }}>
                          <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: '600', color: '#166534' }}>✓ Habilitar:</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <label style={{ fontSize: '13px', color: '#374151' }}>Operador:</label>
                              <select
                                value={habilitarData.operadorId}
                                onChange={(e) => setHabilitarData({ ...habilitarData, operadorId: e.target.value })}
                                style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
                              >
                                <option value="">Sin asignar</option>
                                {operadores.map((op) => (
                                  <option key={op.id} value={op.id}>{op.name}</option>
                                ))}
                              </select>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <label style={{ fontSize: '13px', color: '#374151' }}>Fecha:</label>
                              <input
                                type="date"
                                value={habilitarData.fechaProgramada}
                                onChange={(e) => setHabilitarData({ ...habilitarData, fechaProgramada: e.target.value })}
                                style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
                              />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <label style={{ fontSize: '13px', color: '#374151' }}>Prioridad:</label>
                              <select
                                value={habilitarData.prioridad}
                                onChange={(e) => setHabilitarData({ ...habilitarData, prioridad: e.target.value })}
                                style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
                              >
                                <option value="">Sin cambios</option>
                                <option value="normal">Normal</option>
                                <option value="prioritaria">Prioritaria</option>
                                <option value="urgente">Urgente</option>
                              </select>
                            </div>
                            <button
                              onClick={confirmarHabilitar}
                              style={{ padding: '8px 16px', background: '#10b981', color: 'white', border: 'none', borderRadius: '4px', fontWeight: '600', cursor: 'pointer' }}
                            >
                              ✓ Confirmar
                            </button>
                            <button
                              onClick={() => setExpandedRow(null)}
                              style={{ padding: '8px 16px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                            >
                              Cancelar
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                    {/* Fila expandida para Editar */}
                    {expandedRow?.id === audit.id && expandedRow?.action === 'edit' && (
                      <tr>
                        <td colSpan={8} style={{ padding: '0', background: '#eff6ff', borderBottom: '2px solid #3b82f6' }}>
                          <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: '600', color: '#1d4ed8' }}>✎ Editar:</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <label style={{ fontSize: '13px', color: '#374151' }}>Título:</label>
                              <input
                                type="text"
                                value={editData.titulo}
                                onChange={(e) => setEditData({ ...editData, titulo: e.target.value })}
                                style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px', width: '200px' }}
                              />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <label style={{ fontSize: '13px', color: '#374151' }}>Fecha:</label>
                              <input
                                type="date"
                                value={editData.fechaSolicitada}
                                onChange={(e) => setEditData({ ...editData, fechaSolicitada: e.target.value })}
                                style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
                              />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <label style={{ fontSize: '13px', color: '#374151' }}>Prioridad:</label>
                              <select
                                value={editData.prioridad}
                                onChange={(e) => setEditData({ ...editData, prioridad: e.target.value as any })}
                                style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
                              >
                                <option value="normal">Normal</option>
                                <option value="prioritaria">Prioritaria</option>
                                <option value="urgente">Urgente</option>
                              </select>
                            </div>
                            <button
                              onClick={confirmarEditar}
                              style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', fontWeight: '600', cursor: 'pointer' }}
                            >
                              💾 Guardar
                            </button>
                            <button
                              onClick={() => setExpandedRow(null)}
                              style={{ padding: '8px 16px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                            >
                              Cancelar
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Editar Auditoría */}
      {showEditModal && selectedAuditForEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Editar Auditoría #{selectedAuditForEdit.id}</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Título</label>
                <input
                  type="text"
                  value={editData.titulo}
                  onChange={(e) => setEditData({ ...editData, titulo: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                <textarea
                  value={editData.descripcion}
                  onChange={(e) => setEditData({ ...editData, descripcion: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Solicitada</label>
                <input
                  type="date"
                  value={editData.fechaSolicitada}
                  onChange={(e) => setEditData({ ...editData, fechaSolicitada: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prioridad</label>
                <select
                  value={editData.prioridad}
                  onChange={(e) => setEditData({ ...editData, prioridad: e.target.value as any })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  <option value="normal">Normal</option>
                  <option value="prioritaria">Prioritaria</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={confirmarEditar}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
              >
                Guardar
              </button>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedAuditForEdit(null);
                }}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Formulario Crear Auditoría */}
      {showCrearAuditoriaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="my-8">
            <CreateProgrammedAuditForm
              user={user}
              onSuccess={() => {
                setShowCrearAuditoriaModal(false);
                cargarDatos();
              }}
              onCancel={() => {
                setShowCrearAuditoriaModal(false);
              }}
            />
          </div>
        </div>
      )}

      {/* Modal Habilitar Tránsito */}
      {showHabilitarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Habilitar Auditoría para Tránsito</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Asignar Operador (opcional)</label>
                <select
                  value={habilitarData.operadorId}
                  onChange={(e) => setHabilitarData({ ...habilitarData, operadorId: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  <option value="">Sin asignar</option>
                  {operadores.length > 0 ? (
                    operadores.map((op) => (
                      <option key={op.id} value={op.id}>
                        {op.name} ({op.email})
                      </option>
                    ))
                  ) : (
                    <option disabled>No hay operadores disponibles</option>
                  )}
                </select>
                {operadores.length === 0 && (
                  <p className="text-xs text-red-600 mt-1">⚠️ No se encontraron operadores</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Programada (opcional)</label>
                <input
                  type="date"
                  value={habilitarData.fechaProgramada}
                  onChange={(e) => setHabilitarData({ ...habilitarData, fechaProgramada: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prioridad (opcional)</label>
                <select
                  value={habilitarData.prioridad}
                  onChange={(e) => setHabilitarData({ ...habilitarData, prioridad: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  <option value="">Sin cambios</option>
                  <option value="normal">Normal</option>
                  <option value="prioritaria">Prioritaria</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={confirmarHabilitar}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
              >
                Confirmar
              </button>
              <button
                onClick={() => {
                  setShowHabilitarModal(false);
                  setHabilitarData({ operadorId: '', fechaProgramada: '', prioridad: '' });
                }}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColaGestionPage;
